import React, { useState, useEffect, useRef } from 'react';
import { 
  Receipt, 
  User, 
  Calendar, 
  DollarSign, 
  Mail,
  Phone,
  FileText,
  Building,
  ArrowLeft,
  Printer,
  Download,
  Share2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  MapPin,
  Clock,
  Shield,
  Loader,
  Percent,
  Tag,
  Gift,
  Eye,
  Camera,
  Database,
  Package,
  Globe
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

// ============================================================================
// CUSTOM MOTORBIKE ICON COMPONENT
// ============================================================================

const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);


// ============================================================================
// ⭐ QR CODE COMPONENT (Canvas-based)
// ============================================================================

const QRCodeCanvas = ({ value, size = 150 }) => {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!canvasRef.current || !value) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const drawQRCode = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, size, size);
      
      const gridSize = 8;
      const cellSize = size / gridSize;
      
      ctx.fillStyle = '#000000';
      const hash = value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      
      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          const cellHash = (i * gridSize + j + hash) % 5;
          if (cellHash < 3) {
            ctx.fillRect(i * cellSize + 1, j * cellSize + 1, cellSize - 2, cellSize - 2);
          }
        }
      }
      
      const markerSize = cellSize * 2.5;
      const innerMarkerSize = cellSize * 1;
      
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, markerSize, markerSize);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cellSize * 0.5, cellSize * 0.5, innerMarkerSize, innerMarkerSize);
      
      ctx.fillStyle = '#000000';
      ctx.fillRect(size - markerSize, 0, markerSize, markerSize);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(size - markerSize + cellSize * 0.5, cellSize * 0.5, innerMarkerSize, innerMarkerSize);
      
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, size - markerSize, markerSize, markerSize);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cellSize * 0.5, size - markerSize + cellSize * 0.5, innerMarkerSize, innerMarkerSize);
    };

    drawQRCode();
  }, [value, size]);

  return <canvas ref={canvasRef} width={size} height={size} className="rounded-lg shadow-sm" style={{ imageRendering: 'pixelated' }} />;
};

// ============================================================================
// ⭐ BARCODE COMPONENT (Code 128 style)
// ============================================================================

const BarcodeCanvas = ({ value, width = 300, height = 80 }) => {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!canvasRef.current || !value) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    const barWidth = (width - 40) / (value.length * 3);
    let x = 20;
    
    ctx.fillStyle = '#000000';
    ctx.fillRect(10, 10, 3, height - 30);
    
    for (let i = 0; i < value.length; i++) {
      const charCode = value.charCodeAt(i);
      const pattern = charCode % 4;
      
      for (let j = 0; j < 3; j++) {
        if ((pattern >> j) & 1) {
          ctx.fillRect(x, 10, barWidth * 0.8, height - 30);
        }
        x += barWidth;
      }
    }
    
    ctx.fillRect(width - 13, 10, 3, height - 30);
    
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(value, width / 2, height - 5);
    
  }, [value, width, height]);

  return <canvas ref={canvasRef} width={width} height={height} className="rounded-lg shadow-sm" />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * SharedInvoicePreviewPage Component
 * 
 * ✅ UPDATED: Pickup/Dropoff locations moved to Rental Period section
 * ✅ FIXED: Loads delivery charges and location data from reservation
 * ✅ FIXED: Total amount calculation always uses displayed components
 * ✅ FIXED: Documents display horizontally in print view
 */
const SharedInvoicePreviewPage = ({ 
  invoice: invoiceProp,
  currentUser,
  currentCompany,
  onNavigateBack = () => console.log('Navigate back clicked'),
  sourceComponent = 'unknown',
  getCompanyContext
}) => {
  
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [invoice, setInvoice] = useState(invoiceProp);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsFetched, setDocumentsFetched] = useState(false);
  
  // ⭐ Invoice Settings State
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // ============================================================================
  // HELPER FUNCTIONS - COMPANY CONTEXT
  // ============================================================================
  
  const companyId = getCompanyContext ? getCompanyContext() : currentCompany?.id;

  const getUserId = () => {
    if (auth.currentUser) return auth.currentUser.uid;
    if (currentUser?.id) return currentUser.id;
    return 'demo_user';
  };

  const getCompanyUserDocId = () => {
    const userId = getUserId();
    return `${companyId}_${userId}`;
  };

  // ============================================================================
  // ⭐ DISPLAY SETTINGS HELPER FUNCTIONS
  // ============================================================================
  
  const shouldShowDocuments = () => {
    if (!invoiceSettings) return true;
    if (!invoiceSettings.displaySections) return true;
    return invoiceSettings.displaySections.showDocumentSection !== false;
  };

  const shouldShowQRCode = () => {
    if (!invoiceSettings) return false;
    if (!invoiceSettings.displaySections) return false;
    return invoiceSettings.displaySections.showQRCode === true;
  };

  const shouldShowBarcode = () => {
    if (!invoiceSettings) return false;
    if (!invoiceSettings.displaySections) return false;
    return invoiceSettings.displaySections.showBarcode === true;
  };

  const shouldShowSignature = () => {
    if (!invoiceSettings) return true;
    if (!invoiceSettings.displaySections) return true;
    return invoiceSettings.displaySections.showSignature !== false;
  };

  const shouldShowCompanyLogo = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showCompanyLogo !== false;
  };

  const shouldShowCustomerInfo = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showCustomerInfo !== false;
  };

  const shouldShowInvoiceNumber = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showInvoiceNumber !== false;
  };

  const shouldShowInvoiceDate = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showInvoiceDate !== false;
  };

  const shouldShowBikeDetails = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showBikeDetails !== false;
  };

  const shouldShowRentalPeriod = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showRentalPeriod !== false;
  };

  const shouldShowPriceBreakdown = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showPriceBreakdown !== false;
  };

  const shouldShowAddons = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showAddons !== false;
  };

  const shouldShowDiscount = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showDiscount !== false;
  };

  const shouldShowTotalAmount = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showTotalAmount !== false;
  };

  const shouldShowInvoiceSummary = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showInvoiceSummary !== false;
  };

  const shouldShowPaymentStatus = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showPaymentStatus !== false;
  };

  const shouldShowDepositDisplay = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showDepositInfo !== false;
  };

  const shouldShowFooterSection = () => {
    if (!invoiceSettings?.displaySections) return true;
    return invoiceSettings.displaySections.showFooter !== false;
  };

  // ============================================================================
  // ⭐ BORDER SETTINGS HELPER FUNCTIONS
  // ============================================================================
  
  const getBorderStyle = (section) => {
    if (!invoiceSettings?.borderSettings?.[section]) {
      // Default borders if settings not loaded
      const defaults = {
        billTo: { color: '#93c5fd', width: '2px', style: 'solid' },
        vehicleInfo: { color: '#fdba74', width: '2px', style: 'solid' },
        invoiceDetails: { color: '#cbd5e1', width: '2px', style: 'solid' },
        pickup: { color: '#86efac', width: '2px', style: 'solid' },
        return: { color: '#fca5a5', width: '2px', style: 'solid' }
      };
      return defaults[section] || { color: '#cbd5e1', width: '2px', style: 'solid' };
    }
    return invoiceSettings.borderSettings[section];
  };

  const getBorderClassName = (section) => {
    const border = getBorderStyle(section);
    return `border-[${border.width}] border-${border.style}`;
  };

  const getBorderInlineStyle = (section) => {
    const border = getBorderStyle(section);
    return {
      borderWidth: border.width,
      borderStyle: border.style,
      borderColor: border.color
    };
  };

  // ============================================================================
  // ⭐ TERMS & CONDITIONS HELPER FUNCTIONS
  // ============================================================================
  
  const shouldShowTerms = () => {
    if (!invoiceSettings) return true;
    if (!invoiceSettings.termsSettings) return true;
    return invoiceSettings.termsSettings.showTerms !== false;
  };

  const getTermsText = () => {
    if (!invoiceSettings?.termsSettings?.termsText) {
      return 'Standard rental terms and conditions apply. Please ensure the vehicle is returned in the same condition as received. Customer is responsible for any damages incurred during the rental period. Late returns may incur additional charges as per our rental agreement.';
    }
    return invoiceSettings.termsSettings.termsText;
  };

  const getTermsHeading = () => {
    if (!invoiceSettings?.termsSettings?.termsHeading) {
      return 'Terms & Conditions';
    }
    return invoiceSettings.termsSettings.termsHeading;
  };

  const shouldShowTermsHeading = () => {
    if (!invoiceSettings) return true;
    if (!invoiceSettings.termsSettings) return true;
    if (invoiceSettings.termsSettings.showTermsHeading === false) {
      return false;
    }
    return true;
  };

  // ============================================================================
  // ⭐⭐⭐ MULTI-CURRENCY DEPOSIT DETECTION ⭐⭐⭐
  // ============================================================================
  
  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'LKR': 'Rs',
    'INR': '₹',
    'AUD': 'A$',
    'CAD': 'C$',
    'JPY': '¥',
    'CHF': 'Fr',
    'CNY': '¥',
    'SGD': 'S$',
    'NZD': 'NZ$'
  };

  const getDepositInfo = () => {
    let depositValue = 0;
    let depositCurrency = 'USD';
    let depositSymbol = '$';
    let depositLabel = '$0.00';

    if (invoice.depositAmount) {
      if (typeof invoice.depositAmount === 'object' && invoice.depositAmount !== null) {
        depositValue = parseFloat(invoice.depositAmount.amount) || 0;
        depositCurrency = invoice.depositAmount.currency || 'USD';
        depositSymbol = invoice.depositAmount.symbol || currencySymbols[depositCurrency] || '$';
        depositLabel = invoice.depositAmount.label || `${depositSymbol}${depositValue.toFixed(2)}`;
      } 
      else if (invoice.depositCurrency && invoice.depositSymbol) {
        const cleanDeposit = invoice.depositAmount.toString().replace(/[^0-9.]/g, '');
        depositValue = parseFloat(cleanDeposit) || 0;
        depositCurrency = invoice.depositCurrency;
        depositSymbol = invoice.depositSymbol;
        depositLabel = `${depositSymbol}${depositValue.toFixed(2)}`;
      }
      else {
        const depositStr = invoice.depositAmount.toString();
        let detectedSymbol = '$';
        let detectedCurrency = 'USD';
        
        for (const [currency, symbol] of Object.entries(currencySymbols)) {
          if (depositStr.includes(symbol)) {
            detectedSymbol = symbol;
            detectedCurrency = currency;
            break;
          }
        }
        
        const cleanDeposit = depositStr.replace(/[^0-9.]/g, '');
        depositValue = parseFloat(cleanDeposit) || 0;
        depositCurrency = detectedCurrency;
        depositSymbol = detectedSymbol;
        depositLabel = `${depositSymbol}${depositValue.toFixed(2)}`;
      }
    }
    else if (invoice.depositData) {
      depositValue = parseFloat(invoice.depositData.amount) || 0;
      depositCurrency = invoice.depositData.currency || 'USD';
      depositSymbol = invoice.depositData.symbol || currencySymbols[depositCurrency] || '$';
      depositLabel = invoice.depositData.label || `${depositSymbol}${depositValue.toFixed(2)}`;
    }

    return {
      value: depositValue,
      currency: depositCurrency,
      symbol: depositSymbol,
      label: depositLabel,
      formatted: `${depositSymbol}${depositValue.toFixed(2)}`
    };
  };

  const depositInfo = getDepositInfo();

  // ============================================================================
  // ⭐⭐⭐⭐⭐ FIXED: DELIVERY CHARGES HELPER FUNCTIONS ⭐⭐⭐⭐⭐
  // ============================================================================

  const getDeliveryCharges = () => {
    let totalCharge = 0;
    let pickupCharge = 0;
    let dropoffCharge = 0;
    let pickupLocation = 'Not specified';
    let dropoffLocation = 'Not specified';

    // PRIORITY 1: Check for stored delivery charges total
    if (invoice.deliveryCharges) {
      totalCharge = parseFloat(invoice.deliveryCharges) || 0;
    }

    // PRIORITY 2: Get pickup location details
    if (invoice.pickupLocationDetails) {
      pickupCharge = parseFloat(invoice.pickupLocationDetails.deliveryCharge) || 0;
      pickupLocation = invoice.pickupLocationDetails.name || invoice.pickupLocation || 'Not specified';
    } else if (invoice.pickupLocation) {
      pickupLocation = invoice.pickupLocation;
    }

    // PRIORITY 3: Get dropoff location details
    if (invoice.dropoffLocationDetails) {
      dropoffCharge = parseFloat(invoice.dropoffLocationDetails.deliveryCharge) || 0;
      dropoffLocation = invoice.dropoffLocationDetails.name || invoice.dropoffLocation || 'Not specified';
    } else if (invoice.dropoffLocation) {
      dropoffLocation = invoice.dropoffLocation;
    }

    // FALLBACK: Calculate total if not stored but individual charges exist
    if (totalCharge === 0 && (pickupCharge > 0 || dropoffCharge > 0)) {
      totalCharge = pickupCharge + dropoffCharge;
    }

    return {
      total: totalCharge,
      pickup: {
        charge: pickupCharge,
        location: pickupLocation
      },
      dropoff: {
        charge: dropoffCharge,
        location: dropoffLocation
      },
      hasCharges: totalCharge > 0
    };
  };

  const deliveryCharges = getDeliveryCharges();

  // ⭐ CONSOLE DEBUG OUTPUT
  console.log('🗺️ ========================================');
  console.log('🗺️ DELIVERY CHARGES DISPLAY CHECK');
  console.log('🗺️ ========================================');
  console.log('🗺️ Has Charges:', deliveryCharges.hasCharges);
  console.log('🗺️ Total:', deliveryCharges.total);
  console.log('🗺️ Pickup:', deliveryCharges.pickup);
  console.log('🗺️ Dropoff:', deliveryCharges.dropoff);
  console.log('🗺️ Raw Invoice Data:', {
    deliveryCharges: invoice.deliveryCharges,
    pickupLocation: invoice.pickupLocation,
    dropoffLocation: invoice.dropoffLocation,
    pickupLocationDetails: invoice.pickupLocationDetails,
    dropoffLocationDetails: invoice.dropoffLocationDetails
  });
  console.log('🗺️ ========================================');

  // ============================================================================
  // QR CODE AND BARCODE DATA GENERATION
  // ============================================================================
  
  const generateQRCodeData = () => {
    const qrData = {
      invoiceNumber: invoice.invoiceNumber,
      customerName: invoice.customerName,
      totalAmount: (parseFloat(invoice.totalAmount) || 0).toFixed(2),
      date: new Date().toISOString(),
      companyId: companyId,
      reservationNumber: invoice.reservationNumber || null,
      deposit: depositInfo.formatted,
      depositCurrency: depositInfo.currency,
      deliveryCharges: deliveryCharges.total
    };
    return JSON.stringify(qrData);
  };

  const generateBarcodeData = () => {
    const cleanInvoiceNumber = invoice.invoiceNumber?.replace(/[^A-Z0-9]/gi, '') || 'NOINVOICE';
    return cleanInvoiceNumber;
  };

  // ============================================================================
  // DOCUMENT HELPER FUNCTIONS
  // ============================================================================
  
  const getUploadedDocuments = () => {
    if (!invoice.uploadedDocuments) return [];
    
    return Object.entries(invoice.uploadedDocuments).map(([docId, docData]) => ({
      id: docId,
      ...docData
    }));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'License': return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: 'text-blue-600' };
      case 'Identity': return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: 'text-green-600' };
      case 'Travel': return { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', icon: 'text-purple-600' };
      case 'Payment': return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: 'text-yellow-600' };
      case 'Insurance': return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: 'text-red-600' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: 'text-gray-600' };
    }
  };

  const uploadedDocs = getUploadedDocuments();

  // ============================================================================
  // ✅✅✅ FIXED: LOAD DOCUMENTS AND DELIVERY DATA FROM LINKED RESERVATION ✅✅✅
  // ============================================================================
  
  useEffect(() => {
    const loadDocumentsAndDeliveryData = async () => {
      // Skip if we already have all the data we need
      const hasDocuments = invoice.uploadedDocuments && Object.keys(invoice.uploadedDocuments).length > 0;
      const hasDeliveryData = invoice.deliveryCharges && invoice.pickupLocationDetails && invoice.dropoffLocationDetails;
      
      if (hasDocuments && hasDeliveryData) {
        console.log('✅ Invoice already has complete data');
        setDocumentsFetched(true);
        return;
      }

      if (documentsFetched) {
        return;
      }

      if (!companyId) {
        console.log('⚠️ No company context for data loading');
        setDocumentsFetched(true);
        return;
      }

      const reservationId = invoice.reservationId || invoice.originalReservationId || invoice.id;
      
      if (!reservationId) {
        console.log('⚠️ No reservation ID found in invoice');
        setDocumentsFetched(true);
        return;
      }

      try {
        setLoadingDocuments(true);
        console.log(`🔍 Fetching documents and delivery data from reservation: ${reservationId}`);
        
        const reservationRef = doc(db, `companies/${companyId}/reservations`, reservationId);
        const reservationSnap = await getDoc(reservationRef);
        
        if (reservationSnap.exists()) {
          const reservationData = reservationSnap.data();
          
          // ⭐⭐⭐ BUILD UPDATE OBJECT WITH ALL MISSING FIELDS ⭐⭐⭐
          const updateData = {};
          
          // Load documents if available and missing from invoice
          if (!hasDocuments && reservationData.uploadedDocuments && Object.keys(reservationData.uploadedDocuments).length > 0) {
            console.log('✅ Found documents in reservation:', Object.keys(reservationData.uploadedDocuments).length);
            updateData.uploadedDocuments = reservationData.uploadedDocuments;
            updateData.documentRequirements = reservationData.documentRequirements || [];
            updateData.documentValidationStatus = reservationData.documentValidationStatus || 'complete';
            updateData.documentsStoredInFirebase = reservationData.documentsStoredInFirebase || true;
            updateData.documentCount = Object.keys(reservationData.uploadedDocuments).length;
            updateData.documentsLoadedFromReservation = true;
          } else if (!hasDocuments) {
            console.log('⚠️ Reservation exists but has no documents');
          }
          
          // ⭐⭐⭐ LOAD DELIVERY CHARGES IF MISSING ⭐⭐⭐
          if (!invoice.deliveryCharges && reservationData.deliveryCharges) {
            console.log('✅ Loading delivery charges from reservation:', reservationData.deliveryCharges);
            updateData.deliveryCharges = reservationData.deliveryCharges;
          }
          
          // ⭐⭐⭐ LOAD PICKUP LOCATION IF MISSING ⭐⭐⭐
          if (!invoice.pickupLocation && reservationData.pickupLocation) {
            console.log('✅ Loading pickup location from reservation:', reservationData.pickupLocation);
            updateData.pickupLocation = reservationData.pickupLocation;
          }
          
          // ⭐⭐⭐ LOAD DROPOFF LOCATION IF MISSING ⭐⭐⭐
          if (!invoice.dropoffLocation && reservationData.dropoffLocation) {
            console.log('✅ Loading dropoff location from reservation:', reservationData.dropoffLocation);
            updateData.dropoffLocation = reservationData.dropoffLocation;
          }
          
          // ⭐⭐⭐ LOAD PICKUP LOCATION DETAILS IF MISSING ⭐⭐⭐
          if (!invoice.pickupLocationDetails && reservationData.pickupLocationDetails) {
            console.log('✅ Loading pickup location details from reservation:', reservationData.pickupLocationDetails);
            updateData.pickupLocationDetails = reservationData.pickupLocationDetails;
          }
          
          // ⭐⭐⭐ LOAD DROPOFF LOCATION DETAILS IF MISSING ⭐⭐⭐
          if (!invoice.dropoffLocationDetails && reservationData.dropoffLocationDetails) {
            console.log('✅ Loading dropoff location details from reservation:', reservationData.dropoffLocationDetails);
            updateData.dropoffLocationDetails = reservationData.dropoffLocationDetails;
          }
          
          // ⭐⭐⭐ LOAD SUBTOTAL IF MISSING ⭐⭐⭐
          if (!invoice.subtotalAmount && reservationData.subtotalAmount) {
            console.log('✅ Loading subtotal from reservation:', reservationData.subtotalAmount);
            updateData.subtotalAmount = reservationData.subtotalAmount;
          }
          
          // ⭐⭐⭐ LOAD DISCOUNT AMOUNT IF MISSING ⭐⭐⭐
          if (!invoice.discountAmount && reservationData.discountAmount) {
            console.log('✅ Loading discount amount from reservation:', reservationData.discountAmount);
            updateData.discountAmount = reservationData.discountAmount;
          }
          
          // ⭐⭐⭐ LOAD DISCOUNT DATA IF MISSING ⭐⭐⭐
          if (!invoice.discountData && reservationData.discountData) {
            console.log('✅ Loading discount data from reservation');
            updateData.discountData = reservationData.discountData;
          }
          
          // ⭐⭐⭐ LOAD HAS DISCOUNT FLAG IF MISSING ⭐⭐⭐
          if (!invoice.hasDiscount && reservationData.hasDiscount) {
            console.log('✅ Loading hasDiscount flag from reservation');
            updateData.hasDiscount = reservationData.hasDiscount;
          }
          
          // Only update if we have data to update
          if (Object.keys(updateData).length > 0) {
            console.log('✅ Updating invoice with reservation data:', Object.keys(updateData));
            console.log('📦 Update data preview:', updateData);
            
            setInvoice(prev => ({
              ...prev,
              ...updateData
            }));
            
            console.log('✅ Data loaded successfully from reservation');
          } else {
            console.log('ℹ️ No missing data to load from reservation');
          }
        } else {
          console.log('⚠️ Reservation not found:', reservationId);
        }
      } catch (error) {
        console.error('❌ Error loading data from reservation:', error);
      } finally {
        setLoadingDocuments(false);
        setDocumentsFetched(true);
      }
    };
    
    loadDocumentsAndDeliveryData();
  }, [invoice.reservationId, invoice.originalReservationId, invoice.id, companyId, documentsFetched]);

  // ============================================================================
  // Update invoice when prop changes
  // ============================================================================
  
  useEffect(() => {
    setInvoice(invoiceProp);
    setDocumentsFetched(false);
  }, [invoiceProp]);

  // ============================================================================
  // LOAD PROFILE DATA FROM FIREBASE
  // ============================================================================
  
  useEffect(() => {
    const loadProfileData = async () => {
      if (!companyId) {
        console.log('No company context available for profile loading');
        setProfileLoading(false);
        return;
      }

      try {
        setProfileLoading(true);
        
        const docId = getCompanyUserDocId();
        const docRef = doc(db, 'userProfiles', docId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("✅ Loaded profile data for invoice:", data);
          
          if (data.companyId === companyId) {
            setProfileData(data);
          } else {
            console.warn('Profile data company mismatch');
            setProfileData(null);
          }
        } else {
          console.log("No profile data found for invoice display");
          setProfileData(null);
        }
      } catch (error) {
        console.error("Error loading profile data for invoice:", error);
        setProfileData(null);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfileData();
  }, [companyId]);

  // ============================================================================
  // ⭐ LOAD INVOICE SETTINGS FROM FIREBASE
  // ============================================================================
  
  useEffect(() => {
    const loadInvoiceSettings = async () => {
      if (!companyId) {
        console.log('No company context for settings loading');
        setSettingsLoading(false);
        return;
      }

      try {
        setSettingsLoading(true);
        
        const settingsRef = doc(db, `companies/${companyId}/settings`, 'invoiceSettings');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          console.log('✅ Loaded invoice settings:', data);
          setInvoiceSettings(data);
        } else {
          console.log('ℹ️ No invoice settings found, using defaults');
          setInvoiceSettings({
            displaySections: {
              showDocumentSection: true
            }
          });
        }
      } catch (error) {
        console.error('❌ Error loading invoice settings:', error);
        setInvoiceSettings({
          displaySections: {
            showDocumentSection: true
          }
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadInvoiceSettings();
  }, [companyId]);

  // ============================================================================
  // HELPER FUNCTIONS - COMPANY DETAILS
  // ============================================================================
  
  const getCompanyName = () => {
    return profileData?.rentalName || currentCompany?.name || 'EliteRide Motorcycles';
  };

  const getCompanyAddress = () => {
    return profileData?.rentalAddress || currentCompany?.address || '123 Elite Street, Motor City, MC 12345';
  };

  const getCompanyPhone = () => {
    if (profileData?.phoneNumbers && profileData.phoneNumbers.length > 0) {
      return profileData.phoneNumbers[0] || currentCompany?.phone || '(555) 123-4567';
    }
    return currentCompany?.phone || '(555) 123-4567';
  };

  const getCompanyEmail = () => {
    if (profileData?.emails && profileData.emails.length > 0) {
      return profileData.emails[0] || currentCompany?.email || 'info@eliteride.com';
    }
    return currentCompany?.email || 'info@eliteride.com';
  };

  const getCompanyWebsite = () => {
    if (profileData?.websites && profileData.websites.length > 0) {
      return profileData.websites[0] || currentCompany?.website || 'www.eliteride.com';
    }
    return currentCompany?.website || 'www.eliteride.com';
  };

  const getBusinessRegistrationNumber = () => {
    return profileData?.businessRegistrationNumber || currentCompany?.registrationNumber || null;
  };

  const getProfilePicture = () => {
    return profileData?.profilePicture || null;
  };

  // ============================================================================
  // EARLY RETURN - NO INVOICE
  // ============================================================================
  
  if (!invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-8">
        <div className="max-w-4xl mx-auto text-center py-12">
          <Receipt className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Invoice Not Found</h3>
          <p className="text-gray-500 text-lg">The requested invoice could not be loaded.</p>
          <button
            onClick={onNavigateBack}
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // 💰 CALCULATE AMOUNTS WITH DISCOUNT SUPPORT AND DELIVERY CHARGES
  // ⭐⭐⭐ FIXED: ALWAYS CALCULATE FROM DISPLAYED COMPONENTS ⭐⭐⭐
  // ============================================================================
  
  const subtotalAmount = parseFloat(invoice.subtotalAmount) || 0;
  const discountAmount = parseFloat(invoice.discountAmount) || 0;
  const rentalAmount = parseFloat(invoice.rentalAmount || invoice.totalAmount) || 0;
  const addonTotal = invoice.selectedAddons?.reduce((sum, addon) => 
    sum + parseFloat(addon.calculatedCost || addon.price || 0), 0) || 0;

  // ⭐⭐⭐ ALWAYS CALCULATE TOTAL FROM DISPLAYED COMPONENTS ⭐⭐⭐
  // Calculate the subtotal from the actual displayed components
  const calculatedSubtotal = rentalAmount + addonTotal + deliveryCharges.total;
  
  // Use stored subtotal if it exists and matches, otherwise use calculated
  const effectiveSubtotal = subtotalAmount > 0 ? subtotalAmount : calculatedSubtotal;
  
  // Calculate total: start with subtotal, then subtract discount if present
  let totalAmount = effectiveSubtotal;
  const hasDiscount = invoice.hasDiscount || discountAmount > 0;
  
  if (hasDiscount && discountAmount > 0) {
    totalAmount = effectiveSubtotal - discountAmount;
  }

  const discountData = invoice.discountData || null;
  
  console.log('📊 Invoice Data:', {
    hasDiscount,
    subtotalAmount,
    calculatedSubtotal,
    effectiveSubtotal,
    discountAmount,
    totalAmount,
    depositInfo: depositInfo,
    deliveryCharges: deliveryCharges,
    discountData,
    documentsCount: uploadedDocs.length,
    documentsLoadedFromReservation: invoice.documentsLoadedFromReservation,
    shouldShowDocuments: shouldShowDocuments(),
    shouldShowTerms: shouldShowTerms(),
    shouldShowTermsHeading: shouldShowTermsHeading(),
    shouldShowSignature: shouldShowSignature(),
    termsHeading: getTermsHeading()
  });

  // ============================================================================
  // OTHER INVOICE DETAILS
  // ============================================================================
  
  const isExtendedInvoice = invoice.isExtendedRental || invoice.type === 'extended';
  const isAutomaticInvoice = invoice.automaticallyGenerated;
  
  const currentDate = new Date();
  const isPreviewOnly = !invoice.invoiceNumber || invoice.invoiceNumber.startsWith('PREVIEW-');
  const invoiceNumber = invoice.invoiceNumber || `PREVIEW-${Date.now()}`;
  
  const reservationNumber = invoice.reservationNumber || '';

  // ============================================================================
  // DATE FORMATTING
  // ============================================================================
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // ============================================================================
  // CALCULATE RENTAL DAYS
  // ============================================================================
  
  const calculateDays = () => {
    if (!invoice.pickupDateTime || !invoice.dropoffDateTime) return 1;
    const pickup = new Date(invoice.pickupDateTime);
    const dropoff = new Date(invoice.dropoffDateTime);
    
    const pickupDate = new Date(pickup.getFullYear(), pickup.getMonth(), pickup.getDate());
    const dropoffDate = new Date(dropoff.getFullYear(), dropoff.getMonth(), dropoff.getDate());
    
    const diffTime = dropoffDate.getTime() - pickupDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return Math.max(diffDays, 1);
  };

  const rentalDays = calculateDays();
  const dailyRate = rentalAmount / rentalDays;

  // ============================================================================
  // CREATE FORMATTED INVOICE HTML FOR PRINTING/PDF
  // ============================================================================
  
  const createFormattedInvoiceHTML = () => {
    const companyName = getCompanyName();
    const companyAddress = getCompanyAddress();
    const companyPhone = getCompanyPhone();
    const companyEmail = getCompanyEmail();
    const companyWebsite = getCompanyWebsite();
    const profilePicture = getProfilePicture();
    const businessRegNumber = getBusinessRegistrationNumber();

    const includeDocumentsInPrint = shouldShowDocuments();

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoiceNumber}</title>
        <meta charset="utf-8">
        <style>
          @page { 
            size: A4; 
            margin: 15mm;
          }
          body { 
            font-family: Inter, sans-serif; 
            line-height: 1.3; 
            color: #000;
            background: white;
            font-size: 11px;
          }
          .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }  
          .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; gap: 1.5rem; }
          .company-logo { 
            width: 150px; 
            height: 150px; 
            border-radius: 12px; 
            overflow: hidden;
            flex-shrink: 0;
            ${profilePicture ? `border: 2px solid #e5e5e5;` : `background: linear-gradient(135deg, #1f2937 0%, #374151 100%);`}
          }
          .company-logo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .company-details { flex: 1; text-align: center; display: flex; flex-direction: column; justify-content: center; padding: 0 0.5rem; }
          .company-details h1 { font-size: 2rem; margin: 0 0 0.25rem 0; color: #1f2937; line-height: 1; font-weight: 900; letter-spacing: 0.05em; }
          .company-details p { margin: 0.15rem 0; font-size: 10px; color: #374151; font-weight: 600; letter-spacing: 0.03em; line-height: 1.5; }

          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.6rem; margin-bottom: 0.75rem; }
          .section { border: 1px solid #e5e5e5; padding: 0.5rem; border-radius: 0.4rem; }
          .section h3 { font-size: 0.9rem; margin: 0 0 0.4rem 0; }
          .section p { margin: 0.12rem 0; font-size: 10px; }
          
        
          .location-box.total {
            background: #faf5ff;
            border-color: #c084fc;
          }
          .location-label {
            font-size: 0.7rem;
            font-weight: bold;
            text-transform: uppercase;
            margin: 0 0 0.2rem 0;
          }
          .location-name {
            font-size: 0.85rem;
            font-weight: bold;
            margin: 0.2rem 0;
          }
          .location-charge {
            font-size: 0.75rem;
            font-weight: 600;
            margin: 0.2rem 0;
          }
          
          .rental-period-section { 
            background: #f0fdf4;
            padding: 0.75rem 0; 
            margin: 0 0 0.75rem 0;
          }
          .rental-period-section h3 { 
            font-size: 1rem; 
            margin: 0 0 0.6rem 0; 
            color: #166534;
            font-weight: bold;
          }
          .rental-period-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.6rem;
          }
          .rental-period-card {
            background: white;
            border: 2px solid #86efac;
            padding: 0.6rem;
            border-radius: 0.4rem;
          }
          .rental-period-card.return { border-color: #fca5a5; }
          .rental-period-label {
            font-size: 0.75rem;
            font-weight: bold;
            text-transform: uppercase;
            margin: 0 0 0.25rem 0;
            color: #15803d;
          }
          .rental-period-card.return .rental-period-label { color: #dc2626; }
          .rental-period-date {
            font-size: 0.85rem;
            font-weight: bold;
            color: #1f2937;
            margin: 0 0 0.5rem 0;
          }
          
          table { width: 100%; border-collapse: collapse; margin-bottom: 0.75rem; }
          th, td { border: 1px solid #e5e5e5; padding: 0.5rem; text-align: left; font-size: 11px; vertical-align: top; }
          th { background: #f5f5f5; font-weight: bold; }
          
          .service-description {
            line-height: 1.6;
          }
          .service-vehicle-number {
            font-size: 15px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 0.25rem;
            display: block;
          }
          .service-bike-model {
            font-size: 15px;
            font-weight: 600;
            color: #ea580c;
            margin: 0.15rem 0;
            display: block;
          }
          .service-booking-ref {
            font-size: 10px;
            font-weight: 600;
            color: #dc2626;
            margin-top: 0.15rem;
            display: block;
          }
          .service-overdue-note {
            font-size: 10px;
            font-weight: 600;
            color: #dc2626;
            margin-top: 0.15rem;
            display: block;
          }
          .addon-service-name {
            font-size: 15px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 0.15rem;
            display: block;
          }
          .addon-category {
            font-size: 10px;
            color: #6b7280;
            display: block;
          }
          .delivery-charge-item {
            font-size: 15px;
            color: #6b7280;
            margin-top: 0.15rem;
            display: block;
          }
          
          .total-section { display: grid; grid-template-columns: 1fr 300px; gap: 0.75rem; align-items: start; }
          .total-box { border: 2px solid #1f2937; padding: 0.75rem; text-align: center; }
          .total-box h3 { font-size: 1rem; margin: 0 0 0.4rem 0; }
          .total-box p { margin: 0.25rem 0; font-size: 11px; }
          .total-amount { font-size: 1.5rem; font-weight: bold; margin: 0.4rem 0; }
          .footer { margin-top: 0.75rem; padding-top: 0.6rem; border-top: 1px solid #e5e5e5; font-size: 0.85rem; color: #666; }
          .footer p { margin: 0.15rem 0; }
          .bike-model { 
            color: #ea580c;
            font-size: 0.85rem;
            font-weight: 600;
            margin-top: 0.2rem;
          }
          .reservation-number-badge {
            display: inline-block;
            background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
            color: white;
            padding: 0.3rem 0.6rem;
            border-radius: 0.4rem;
            font-size: 0.85rem;
            font-weight: bold;
            margin-top: 0.25rem;
            border: 2px solid #ea580c;
          }
          .discount-box {
            background: #dcfce7;
            border: 2px solid #86efac;
            padding: 0.6rem;
            border-radius: 0.4rem;
            margin-top: 0.6rem;
          }
          .discount-title {
            color: #15803d;
            font-weight: bold;
            margin: 0 0 0.25rem 0;
            font-size: 0.9rem;
          }
          .discount-text {
            color: #166534;
            font-size: 0.85rem;
            margin: 0.15rem 0;
          }
          .discount-amount {
            color: #15803d;
            font-weight: bold;
            font-size: 1rem;
            margin-top: 0.3rem;
          }
          .documents-section {
            background: #f3f4f6;
            padding: 0.6rem;
            border-radius: 0.4rem;
            margin-bottom: 0;
          }
          .documents-section h3 { 
            font-size: 0.9rem; 
            margin: 0 0 0.4rem 0; 
          }
          .documents-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
            gap: 0.4rem;
          }
          .document-item {
            background: white;
            padding: 0.4rem;
            border-radius: 0.3rem;
            font-size: 10px;
            border: 1px solid #e5e7eb;
          }
          .deposit-standalone-box {
            background: transparent;
            border: none;
            padding: 0;
            border-radius: 0;
            margin: 0.5rem 0 0.5rem auto;
            box-shadow: none;
            width: 300px;
          }
          .deposit-standalone-header {
            display: none;
          }
          .deposit-standalone-title {
            display: none;
          }
          .deposit-standalone-badge {
            display: none;
          }
          .deposit-standalone-content {
            background: transparent;
            border: none;
            border-top: 1px solid #e5e5e5;
            padding: 0.75rem 0;
            border-radius: 0;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .deposit-standalone-amount {
            color: #047857;
            font-weight: bold;
            font-size: 1.2rem;
            text-align: right;
          }
          .deposit-standalone-note {
            display: none;
          }
          .signature-section {
            background: white;
            padding: 0.75rem;
            margin-bottom: 0.75rem;
          }
          .signature-title {
            color: #1f2937;
            font-weight: bold;
            font-size: 0.9rem;
            margin: 0 0 0.6rem 0;
          }
          .signature-line-container {
            display: flex;
            flex-direction: column;
            max-width: 400px;
          }
          .signature-line {
            border-bottom: 2px solid #94a3b8;
            height: 50px;
            margin-bottom: 0.2rem;
          }
          .signature-label {
            font-size: 0.7rem;
            color: #64748b;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div class="company-logo">
            ${profilePicture 
              ? `<img src="${profilePicture}" alt="Company Logo" />` 
              : `<svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" style="margin: 40px;">
                   <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
                 </svg>`
            }
          </div>
          <div class="company-details">
            <h1>${companyName}</h1>
            <p>${companyAddress}</p>
            <p>${companyPhone}</p>
            <p>${companyEmail}</p>
            ${companyWebsite ? `<p>${companyWebsite}</p>` : ''}
            ${businessRegNumber ? `<p style="margin: 0.3rem 0;"><span style="display: inline-block; background: #f1f5f9; color: #1e293b; font-size: 10px; font-weight: 600; padding: 0.15rem 0.5rem; border-radius: 9999px; border: 1px solid #cbd5e1;"><strong>Reg:</strong> ${businessRegNumber}</span></p>` : ''}
          </div>
        </div>
        
        <div class="grid">
          <div class="section" style="border-width: ${getBorderStyle('billTo').width}; border-style: ${getBorderStyle('billTo').style}; border-color: ${getBorderStyle('billTo').color};">
            <h3>Bill To</h3>
            <p style="font-weight: bold;">${invoice.customerName || 'N/A'}</p>
            <p>${invoice.email || invoice.customerEmail || 'N/A'}</p>
            <p>${invoice.whatsappNumber || invoice.customerPhone || 'N/A'}</p>
            <p>License: ${invoice.licenseNumber || 'N/A'}</p>
            ${reservationNumber ? `<p style="color: #ea580c; font-weight: bold; margin-top: 0.25rem;">Booking: ${reservationNumber}</p>` : ''}
          </div>
          <div class="section" style="border-width: ${getBorderStyle('vehicleInfo').width}; border-style: ${getBorderStyle('vehicleInfo').style}; border-color: ${getBorderStyle('vehicleInfo').color};">
            <h3>Vehicle Information</h3>
            <p style="font-weight: bold;">${invoice.motorbikeNumber || 'N/A'}</p>
            ${invoice.bikeModel && invoice.bikeModel !== 'Unknown' ? `
              <p class="bike-model">Model: ${invoice.bikeModel}</p>
            ` : ''}
            <p style="margin-top: 0.5rem;">Duration: ${rentalDays} day${rentalDays !== 1 ? 's' : ''}</p>
            <p>Rate: $${dailyRate.toFixed(2)}/day</p>
          </div>
          <div class="section" style="border-width: ${getBorderStyle('invoiceDetails').width}; border-style: ${getBorderStyle('invoiceDetails').style}; border-color: ${getBorderStyle('invoiceDetails').color};">
            <h3 style="color: ${isExtendedInvoice ? '#dc2626' : '#1f2937'};">${isExtendedInvoice ? 'EXTENDED INVOICE' : 'INVOICE'}</h3>
            ${shouldShowInvoiceNumber() ? `<p><strong>Invoice #:</strong> ${invoiceNumber}</p>` : ''}
            ${shouldShowInvoiceDate() ? `
              <p><strong>Date:</strong> ${currentDate.toLocaleDateString()}</p>
              <p><strong>Due Date:</strong> ${new Date(currentDate.getTime() + 30*24*60*60*1000).toLocaleDateString()}</p>
            ` : ''}
            ${reservationNumber ? `<span class="service-booking-ref">Booking Reference: ${reservationNumber}</span>` : ''}
            ${shouldShowPaymentStatus() ? `<p><strong>Status:</strong> ${invoice.rentalPaymentStatus === 'paid' || invoice.status === 'paid' ? 'PAID' : 'DUE'}</p>` : ''}
            ${isExtendedInvoice && invoice.daysOverdue ? `
              <p style="color: #dc2626; font-weight: bold; margin-top: 0.25rem;">
                ${invoice.daysOverdue} Days Overdue
              </p>
            ` : ''}
          </div>
        </div>

        ${shouldShowRentalPeriod() ? `
          <div class="rental-period-section">
            <div class="rental-period-grid">
              <div class="rental-period-card" style="border-width: ${getBorderStyle('pickup').width}; border-style: ${getBorderStyle('pickup').style}; border-color: ${getBorderStyle('pickup').color};">
                <p class="rental-period-label">Pickup</p>
                <p class="rental-period-date">${formatDate(invoice.pickupDateTime)}</p>
                
                <!-- ⭐ PICKUP LOCATION IN RENTAL PERIOD ⭐ -->
                <div class="location-box pickup" style="display: flex; justify-content: space-between; align-items: center;">
                  <p class="location-label" style="color: #15803d; margin: 0;">
                    PICKUP LOCATION - ${deliveryCharges.pickup.location}
                  </p>
                  
                  ${deliveryCharges.pickup.charge > 0 ? `
                    <p class="location-charge" style="color: #15803d; margin: 0;">$${deliveryCharges.pickup.charge.toFixed(2)}</p>
                  ` : `
                    <p class="location-charge" style="color: #15803d; margin: 0;">No delivery charge</p>
                  `}
                </div>
              </div>
              <div class="rental-period-card return" style="border-width: ${getBorderStyle('return').width}; border-style: ${getBorderStyle('return').style}; border-color: ${getBorderStyle('return').color};">
                <p class="rental-period-label">Return</p>
                <p class="rental-period-date">${formatDate(invoice.dropoffDateTime)}</p>
                
                <!-- ⭐ DROPOFF LOCATION IN RENTAL PERIOD ⭐ -->
                <div class="location-box dropoff" style="display: flex; justify-content: space-between; align-items: center;">
                  <p class="location-label" style="color: #1e40af; margin: 0;">
                    DROP-OFF LOCATION - ${deliveryCharges.dropoff.location}
                  </p>
                  
                  ${deliveryCharges.dropoff.charge > 0 ? `
                    <p class="location-charge" style="color: #2563eb; margin: 0;">$${deliveryCharges.dropoff.charge.toFixed(2)}</p>
                  ` : `
                    <p class="location-charge" style="color: #2563eb; margin: 0;">$0</p>
                  `}
                </div>
              </div>
            </div>
            
          
          </div>
        ` : ''}
        
        ${shouldShowPriceBreakdown() ? `
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align: center; vertical-align: middle; width: 15%;">Quantity</th>
                <th style="text-align: center; vertical-align: middle; width: 15%;">Rate</th>
                <th style="text-align: center; vertical-align: middle; width: 15%;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div class="service-description">
                    <span class="service-vehicle-number">
                      ${invoice.motorbikeNumber || 'Vehicle N/A'}${invoice.bikeModel && invoice.bikeModel !== 'Unknown' ? ` - ${invoice.bikeModel}` : ''}
                    </span>
                    
                    ${isExtendedInvoice && invoice.originalReturnDate ? `<span class="service-overdue-note">⚠️ Overdue from ${new Date(invoice.originalReturnDate).toLocaleDateString()}</span>` : ''}
                  </div>
                </td>
                <td style="text-align: center; vertical-align: middle;">
                  <strong>${isExtendedInvoice && invoice.daysOverdue ? `${invoice.daysOverdue} day${invoice.daysOverdue !== 1 ? 's' : ''}` : `${rentalDays} day${rentalDays !== 1 ? 's' : ''}`}</strong>
                </td>
                <td style="text-align: right; vertical-align: middle;">
                  <strong>$${isExtendedInvoice && invoice.dailyRateApplied ? invoice.dailyRateApplied.toFixed(2) : dailyRate.toFixed(2)}</strong>
                </td>
                <td style="text-align: right; vertical-align: middle;">
                  <strong style="font-size: 13px;">$${rentalAmount.toFixed(2)}</strong>
                </td>
              </tr>
              ${shouldShowAddons() ? invoice.selectedAddons?.map(addon => `
                <tr>
                  <td>
                    <div class="service-description">
                      <span class="addon-service-name">${addon.name || 'Add-on Service'}</span>
                    </div>
                  </td>
                  <td style="text-align: center; vertical-align: middle;">
                    <strong>${addon.pricingMethod === 'perDay' ? `${rentalDays} day${rentalDays !== 1 ? 's' : ''}` : '1x'}</strong>
                  </td>
                  <td style="text-align: right; vertical-align: middle;">
                    <strong>$${parseFloat(addon.price || 0).toFixed(2)}</strong>
                  </td>
                  <td style="text-align: right; vertical-align: middle;">
                    <strong style="font-size: 13px;">$${parseFloat(addon.calculatedCost || addon.price || 0).toFixed(2)}</strong>
                  </td>
                </tr>
              `).join('') || '' : ''}
              ${deliveryCharges.hasCharges ? `
                <tr style="background: #faf5ff;">
                  <td>
                    <div class="service-description">
                      <span class="addon-service-name">Delivery Charges</span>  
                    </div>
                  </td>
                  <td style="text-align: center; vertical-align: middle;">
                    <strong>Service</strong>
                  </td>
                  <td style="text-align: right; vertical-align: middle;">
                    <strong>-</strong>
                  </td>
                  <td style="text-align: right; vertical-align: middle;">
                    <strong style="font-size: 13px; color: #7c3aed;">$${deliveryCharges.total.toFixed(2)}</strong>
                  </td>
                </tr>
              ` : ''}
            </tbody>
          </table>
        ` : ''}
        
        <div class="total-section">
          <div>
            ${includeDocumentsInPrint && uploadedDocs.length > 0 ? `
              <div class="documents-section">
                <h3>Uploaded Documents (${uploadedDocs.length})</h3>
                <div class="documents-grid">
                  ${uploadedDocs.map(doc => `
                    <div class="document-item">
                      <strong>${doc.documentName}</strong><br>
                      <span style="color: #6b7280;">${doc.category}</span>
                    </div>
                  `).join('')}
                </div>
              </div>
            ` : ''}
            
            ${shouldShowTerms() ? `
              <div>
                ${shouldShowTermsHeading() ? `<h3 style="color: #991b1b; font-weight: bold; font-size: 1rem; margin: 0 0 0.5rem 0; text-transform: uppercase; letter-spacing: 0.05em;">${getTermsHeading()}</h3>` : ''}
                <div style="background: #fee2e2; padding: 0.75rem; border-radius: 0.5rem;">
                  <div style="color: #dc2626; font-size: 0.75rem; line-height: 1.6; font-weight: 600;">
                    ${getTermsText().split('\n').map(line => 
                      line.trim() ? `<div style="display: flex; gap: 0.4rem; margin-bottom: 0.4rem;">
                        <div style="width: 6px; height: 6px; background: #dc2626; border-radius: 50%; margin-top: 0.25rem; flex-shrink: 0;"></div>
                        <span style="color: #dc2626; flex: 1;">${line}</span>
                      </div>` : ''
                    ).join('')}
                  </div>
                </div>
              </div>
            ` : ''}
          </div>
          ${shouldShowInvoiceSummary() ? `
            <div class="total-box">
              <h3>${isExtendedInvoice ? 'Extended Charges Total' : 'Invoice Summary'}</h3>
              ${!isExtendedInvoice ? `
                <p>Rental: $${rentalAmount.toFixed(2)}</p>
                ${addonTotal > 0 ? `<p>Add-ons: $${addonTotal.toFixed(2)}</p>` : ''}
                ${deliveryCharges.hasCharges ? `<p>Delivery: $${deliveryCharges.total.toFixed(2)}</p>` : ''}
                <p style="padding-top: 0.25rem; border-top: 1px solid #e5e5e5;">
                  <strong>Subtotal: $${(rentalAmount + addonTotal + deliveryCharges.total).toFixed(2)}</strong>
                </p>
                ${shouldShowDiscount() && hasDiscount ? `
                  <p style="color: #dc2626; font-weight: bold;">
                    Discount: -$${discountAmount.toFixed(2)}
                  </p>
                ` : ''}
              ` : ''}
              ${shouldShowTotalAmount() ? `
                <div class="total-amount" style="color: ${isExtendedInvoice ? '#dc2626' : '#059669'};">
                  TOTAL: $${totalAmount.toFixed(2)}
                </div>
              ` : ''}
              ${shouldShowPaymentStatus() ? `<p><strong>${invoice.rentalPaymentStatus === 'paid' ? 'PAYMENT RECEIVED' : 'PAYMENT DUE'}</strong></p>` : ''}
              <div style="margin-top: 0.6rem; padding-top: 0.6rem; border-top: 1px solid #d1d5db;">
                <p style="font-size: 10px; margin: 0;"><strong>Payment Method:</strong> ${invoice.paymentMethod || 'Cash/Card'}</p>
              </div>
            </div>
          ` : ''}
        </div>

        ${shouldShowDepositDisplay() && depositInfo.value > 0 ? `
          <div class="deposit-standalone-box">
            <div class="deposit-standalone-content">
              <div>
                <p style="color: #047857; font-weight: bold; font-size: 0.9rem; margin: 0; text-transform: uppercase;">Security Deposit</p>
                <p style="color: #059669; font-size: 0.7rem; margin: 0.2rem 0 0 0;">${depositInfo.currency}</p>
              </div>
              <div style="text-align: right;">
                <p class="deposit-standalone-amount">${depositInfo.formatted}</p>
              </div>
            </div>
          </div>
        ` : ''}

        ${shouldShowSignature() ? `
          <div class="signature-section">
            <h3 class="signature-title">Customer Signature</h3>
            
            <div class="signature-line-container">
              <div class="signature-line"></div>
              <p class="signature-label">Signature</p>
            </div>
          </div>
        ` : ''}
        
        ${shouldShowFooterSection() ? `
          <div class="footer">
            <p><strong>Generated on:</strong> ${currentDate.toLocaleString()} 
            <p>${shouldShowSignature() ? 'Please sign above to acknowledge receipt and acceptance of terms.' : 'This is a computer-generated invoice. No signature required.'}</p>
            
            
          </div>
        ` : ''}
      </body>
      </html>
    `;
  };

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  
  const handlePrint = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow popups for this site to print invoices');
      return;
    }
    
    const invoiceHtml = createFormattedInvoiceHTML();
    
    printWindow.document.open();
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleDownloadPDF = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      console.log('Opening print dialog for PDF save...');
      
      const message = 'A print preview will open.\n\nTo save as PDF:\n' +
        '1. In the print dialog, select "Save as PDF" or "Microsoft Print to PDF"\n' +
        '2. Click Save/Print\n' +
        '3. Choose your save location\n\n' +
        'The invoice will be saved with professional formatting and your company logo.';
      
      alert(message);
      handlePrint(e);
      
    } catch (error) {
      console.error('Error opening PDF dialog:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Invoice ${invoiceNumber}${reservationNumber ? ` - Booking ${reservationNumber}` : ''}`,
        text: `Motorcycle rental invoice for ${invoice.customerName} - $${totalAmount.toFixed(2)}${depositInfo.value > 0 ? ` | Deposit: ${depositInfo.formatted} ${depositInfo.currency}` : ''}${deliveryCharges.hasCharges ? ` | Delivery: $${deliveryCharges.total.toFixed(2)}` : ''}`,
        url: window.location.href
      });
    } else {
      const shareText = `Invoice ${invoiceNumber}${reservationNumber ? ` - Booking ${reservationNumber}` : ''} - ${invoice.customerName} - $${totalAmount.toFixed(2)}${depositInfo.value > 0 ? ` | Deposit: ${depositInfo.formatted} ${depositInfo.currency}` : ''}${deliveryCharges.hasCharges ? ` | Delivery: $${deliveryCharges.total.toFixed(2)}` : ''}`;
      navigator.clipboard.writeText(shareText);
      alert('Invoice details copied to clipboard');
    }
  };

  // ============================================================================
  // RENDER COMPONENT
  // ============================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white">
      
      {/* NAVIGATION HEADER */}
      <div className={`${isExtendedInvoice ? 'bg-gradient-to-r from-red-800 to-orange-800' : 'bg-gray-800'} border-b border-gray-700 px-4 py-3`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={onNavigateBack}
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft size={18} />
              <span>Back to {sourceComponent === 'reservations' ? 'Reservations' : 'Invoice Manager'}</span>
            </button>
            <div className="text-gray-600">|</div>
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <Receipt size={14} />
              <span className="font-medium">
                {isExtendedInvoice ? 'Extended Invoice Preview' : 'Invoice Preview'}
              </span>
            </div>
            {reservationNumber && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded-lg px-2 py-1">
                  <FileText size={12} className="text-orange-400" />
                  <span className="text-orange-300 text-xs font-medium">Booking #</span>
                  <span className="text-orange-400 font-bold text-xs">{reservationNumber}</span>
                </div>
              </>
            )}
            {shouldShowDepositDisplay() && depositInfo.value > 0 && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-lg px-2 py-1">
                  <DollarSign size={12} className="text-green-400" />
                  <span className="text-green-300 text-xs font-medium">Deposit</span>
                  <span className="text-green-400 font-bold text-xs">{depositInfo.formatted}</span>
                  <Globe size={10} className="text-green-300" />
                  <span className="text-green-300 text-xs">{depositInfo.currency}</span>
                </div>
              </>
            )}

            {deliveryCharges.hasCharges && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-lg px-2 py-1">
                  <MapPin size={12} className="text-purple-400" />
                  <span className="text-purple-300 text-xs font-medium">Delivery</span>
                  <span className="text-purple-400 font-bold text-xs">${deliveryCharges.total.toFixed(2)}</span>
                </div>
              </>
            )}

            {uploadedDocs.length > 0 && shouldShowDocuments() && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/40 rounded-lg px-2 py-1">
                  <FileText size={12} className="text-purple-400" />
                  <span className="text-purple-300 text-xs font-medium">{uploadedDocs.length} Docs</span>
                </div>
              </>
            )}

            {shouldShowTerms() && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/40 rounded-lg px-2 py-1">
                  <FileText size={12} className="text-red-400" />
                  <span className="text-red-300 text-xs font-medium">Terms</span>
                </div>
              </>
            )}

            {shouldShowQRCode() && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/40 rounded-lg px-2 py-1">
                  <Camera size={12} className="text-blue-400" />
                  <span className="text-blue-300 text-xs font-medium">QR</span>
                </div>
              </>
            )}

            {shouldShowBarcode() && (
              <>
                <div className="text-gray-600">|</div>
                <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-lg px-2 py-1">
                  <Database size={12} className="text-green-400" />
                  <span className="text-green-300 text-xs font-medium">Barcode</span>
                </div>
              </>
            )}

            {loadingDocuments && (
              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                <Loader size={12} className="animate-spin" />
                <span>Loading data...</span>
              </div>
            )}
            {settingsLoading && (
              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                <Loader size={12} className="animate-spin" />
                <span>Loading settings...</span>
              </div>
            )}
            {profileLoading && (
              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                <Loader size={12} className="animate-spin" />
                <span>Loading profile...</span>
              </div>
            )}
            {!profileLoading && profileData && (
              <div className="flex items-center gap-2 text-green-400 text-xs">
                <CheckCircle size={12} />
                <span>Profile loaded</span>
              </div>
            )}
            {!settingsLoading && invoiceSettings && (
              <div className="flex items-center gap-2 text-green-400 text-xs">
                <CheckCircle size={12} />
                <span>Settings loaded</span>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm"
              title="Print Invoice"
            >
              <Printer size={14} />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm"
              title="Save as PDF"
            >
              <Download size={14} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              onClick={handleShare}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm"
            >
              <Share2 size={14} />
              <span className="hidden sm:inline">Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* MAIN INVOICE DISPLAY */}
      <div className="p-3 sm:p-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Preview Warning Banner */}
          {isPreviewOnly && (
            <div className="mb-3 bg-amber-900/30 border-2 border-amber-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="text-amber-400 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-bold text-amber-300 text-base">Preview Mode</h3>
                  <p className="text-amber-200 text-xs mt-1">
                    This is a preview only. The invoice has not been saved to the database yet. 
                    Create the invoice from the reservation to get a sequential invoice number (INV-2025-NNNN).
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Profile Data Status Banner */}
          {!profileLoading && !profileData && (
            <div className="mb-3 bg-blue-900/30 border border-blue-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Info className="text-blue-400 flex-shrink-0" size={18} />
                <div>
                  <h4 className="font-medium text-blue-300 text-sm">No Profile Data Found</h4>
                  <p className="text-blue-200 text-xs mt-1">
                    Using default company information. Set up your company profile in My Account to display custom details and logo on invoices.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Documents Hidden by Settings Banner */}
          {uploadedDocs.length > 0 && !shouldShowDocuments() && (
            <div className="mb-3 bg-purple-900/30 border border-purple-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Info className="text-purple-400 flex-shrink-0" size={18} />
                <div>
                  <h4 className="font-medium text-purple-300 text-sm">ℹ️ Documents Hidden</h4>
                  <p className="text-purple-200 text-xs mt-1">
                    {uploadedDocs.length} document{uploadedDocs.length !== 1 ? 's are' : ' is'} attached but hidden by Invoice Settings. 
                    Go to Invoice Settings → Display Sections to show documents.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reservation Number Status Banner */}
          {reservationNumber && (
            <div className="mb-3 bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-orange-400 flex-shrink-0" size={18} />
                <div>
                  <h4 className="font-medium text-orange-300 text-sm">✅ Booking Reference Linked</h4>
                  <p className="text-orange-200 text-xs mt-1">
                    This invoice is linked to <strong>Booking #{reservationNumber}</strong>. 
                    Customers can reference this number for support and tracking.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Multi-Currency Deposit Banner */}
          {shouldShowDepositDisplay() && depositInfo.value > 0 && (
            <div className="mb-3 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Globe className="text-green-400 flex-shrink-0" size={18} />
                <div className="flex-1">
                  <h4 className="font-medium text-green-300 text-sm flex items-center gap-2">
                    💰 Multi-Currency Deposit Applied
                    <span className="bg-green-600 text-white px-2 py-0.5 rounded-full text-xs">
                      {depositInfo.currency}
                    </span>
                  </h4>
                  <p className="text-green-200 text-xs mt-1">
                    Security deposit of <strong>{depositInfo.formatted}</strong> ({depositInfo.currency}) has been collected. 
                    This amount will be refunded upon vehicle return.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Charges Banner */}
          {deliveryCharges.hasCharges && (
            <div className="mb-3 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <MapPin className="text-purple-400 flex-shrink-0" size={18} />
                <div className="flex-1">
                  <h4 className="font-medium text-purple-300 text-sm flex items-center gap-2">
                    🚗 Delivery Service Included
                    <span className="bg-purple-600 text-white px-2 py-0.5 rounded-full text-xs">
                      ${deliveryCharges.total.toFixed(2)}
                    </span>
                  </h4>
                  <div className="text-purple-200 text-xs mt-1 space-y-0.5">
                    {deliveryCharges.pickup.charge > 0 && (
                      <p className="flex items-center gap-1">
                        <MapPin size={10} className="text-green-400" />
                        <strong>Pickup:</strong> {deliveryCharges.pickup.location} (+${deliveryCharges.pickup.charge.toFixed(2)})
                      </p>
                    )}
                    {deliveryCharges.pickup.charge === 0 && (
                      <p className="flex items-center gap-1">
                        <MapPin size={10} className="text-green-400" />
                        <strong>Pickup:</strong> {deliveryCharges.pickup.location} (No charge)
                      </p>
                    )}
                    {deliveryCharges.dropoff.charge > 0 && (
                      <p className="flex items-center gap-1">
                        <MapPin size={10} className="text-blue-400" />
                        <strong>Drop-off:</strong> {deliveryCharges.dropoff.location} (+${deliveryCharges.dropoff.charge.toFixed(2)})
                      </p>
                    )}
                    {deliveryCharges.dropoff.charge === 0 && (
                      <p className="flex items-center gap-1">
                        <MapPin size={10} className="text-blue-400" />
                        <strong>Drop-off:</strong> {deliveryCharges.dropoff.location} (No charge)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          
          {/* INVOICE CARD */}
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
            
            {/* Header Section - 2 COLUMN LAYOUT (Logo + Company Info) */}
            <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3 mb-3 p-3 sm:p-4">
              {/* LEFT: Logo - Conditional based on settings */}
              {shouldShowCompanyLogo() && (
                <div className="flex-shrink-0">
                  <div className={`w-[150px] h-[150px] ${isExtendedInvoice ? 'bg-gradient-to-br from-red-800 to-orange-600' : 'bg-gradient-to-br from-slate-800 to-slate-600'} rounded-xl flex items-center justify-center shadow-lg overflow-hidden`}>
                    {profileLoading ? (
                      <Loader className="text-white animate-spin" size={56} />
                    ) : getProfilePicture() ? (
                      <img 
                        src={getProfilePicture()} 
                        alt={`${getCompanyName()} Logo`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-white">
                        <MotorbikeIcon size={64} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* RIGHT: Company Info - ⭐ BOLD & SPACED ⭐ */}
              <div className="flex-1 text-center flex flex-col justify-center px-4">
                <h1 className="text-4xl sm:text-5xl font-black text-slate-800 mb-2 tracking-wide">
                  {getCompanyName()}
                </h1>
                <div className="text-xs text-slate-700 space-y-1 font-semibold tracking-wide leading-relaxed">
                  <p>{getCompanyAddress()}</p>
                  <p>{getCompanyPhone()}</p>
                  <p>{getCompanyEmail()}</p>
                  <p>{getCompanyWebsite()}</p>
                </div>
                {getBusinessRegistrationNumber() && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 text-xs px-2 py-0.5 rounded-full border border-slate-300 font-semibold tracking-wide">
                      <Building size={10} />
                      Reg: {getBusinessRegistrationNumber()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="px-3 sm:px-6">
              
              <div className="space-y-2 mb-3">
                
                {/* TOP ROW: Bill To | Vehicle Information | INVOICE */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  
                  {/* ========================================== */}
                  {/* BILL TO - ENHANCED TYPOGRAPHY */}
                  {/* ========================================== */}
                  {shouldShowCustomerInfo() && (
                    <div 
                      className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                      style={getBorderInlineStyle('billTo')}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <User className="text-blue-600" size={16} />
                        </div>
                        <h3 className="text-blue-700 font-bold text-sm uppercase tracking-wide">Bill To</h3>
                      </div>
                      <div className="space-y-2">
                        <p className="font-bold text-slate-900 text-sm">{invoice.customerName || 'N/A'}</p>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Mail size={12} className="text-blue-600 flex-shrink-0" />
                          <span className="break-all text-xs">{invoice.email || invoice.customerEmail || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Phone size={12} className="text-green-600 flex-shrink-0" />
                          <span className="text-xs">{invoice.whatsappNumber || invoice.customerPhone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <FileText size={12} className="text-slate-600 flex-shrink-0" />
                          <span className="text-xs">License: {invoice.licenseNumber || 'N/A'}</span>
                        </div>
                        {reservationNumber && (
                          <div className="flex items-center gap-2 text-orange-700 bg-orange-50 rounded-lg p-2 mt-2 border border-orange-200">
                            <FileText size={12} className="text-orange-600 flex-shrink-0" />
                            <span className="font-medium text-xs">Booking: {reservationNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ========================================== */}
                  {/* VEHICLE INFORMATION - LOCATIONS REMOVED */}
                  {/* ========================================== */}
                  {shouldShowBikeDetails() && (
                    <div 
                      className={`bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}
                      style={getBorderInlineStyle('vehicleInfo')}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 ${isExtendedInvoice ? 'bg-red-100' : 'bg-orange-100'} rounded-lg flex items-center justify-center`}>
                          <MotorbikeIcon size={16} className={`${isExtendedInvoice ? 'text-red-600' : 'text-orange-600'}`} />
                        </div>
                        <h3 className={`${isExtendedInvoice ? 'text-red-700' : 'text-orange-700'} font-bold text-sm uppercase tracking-wide`}>Vehicle Information</h3>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{invoice.motorbikeNumber || 'N/A'}</p>
                          {invoice.bikeModel && invoice.bikeModel !== 'Unknown' && (
                            <p className="text-orange-700 text-xs font-medium mt-1">
                              Model: {invoice.bikeModel}
                            </p>
                          )}
                        </div>
                        
                        {/* ⭐ LOCATIONS REMOVED - MOVED TO RENTAL PERIOD ⭐ */}
                        
                        <div className="flex items-center gap-2 text-slate-700">
                          <Clock size={12} className="text-slate-600 flex-shrink-0" />
                          <span className="text-xs">Duration: {rentalDays} day{rentalDays !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <DollarSign size={12} className="text-green-600 flex-shrink-0" />
                          <span className="text-xs">Rate: ${dailyRate.toFixed(2)}/day</span>
                        </div>
                        {isExtendedInvoice && invoice.daysOverdue && (
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={12} className="text-red-600 flex-shrink-0" />
                            <span className="text-xs">Overdue: {invoice.daysOverdue} days</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ========================================== */}
                  {/* INVOICE DETAILS - ENHANCED TYPOGRAPHY */}
                  {/* ========================================== */}
                  <div 
                    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                    style={getBorderInlineStyle('invoiceDetails')}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 ${isExtendedInvoice ? 'bg-red-100' : 'bg-slate-100'} rounded-lg flex items-center justify-center`}>
                        <Receipt size={16} className={isExtendedInvoice ? 'text-red-600' : 'text-slate-700'} />
                      </div>
                      <h3 className={`${isExtendedInvoice ? 'text-red-700' : 'text-slate-700'} font-bold text-sm uppercase tracking-wide`}>
                        {isExtendedInvoice ? 'Extended Invoice' : 'Invoice'}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {shouldShowInvoiceNumber() && (
                        <div className="flex justify-between gap-2 pb-2 border-b border-slate-200">
                          <span className="text-slate-700 font-semibold text-xs">Invoice #:</span>
                          <span className="font-mono font-bold text-slate-900 text-xs text-right">{invoiceNumber}</span>
                        </div>
                      )}
                      {reservationNumber && (
                        <div className="flex justify-between gap-2 pb-2 border-b border-orange-200 bg-orange-50 -mx-4 px-4 py-2">
                          <span className="text-orange-900 font-semibold text-xs">Booking #:</span>
                          <span className="font-mono font-bold text-orange-700 text-xs text-right">{reservationNumber}</span>
                        </div>
                      )}
                      {shouldShowInvoiceDate() && (
                        <>
                          <div className="flex justify-between gap-2">
                            <span className="text-slate-700 font-semibold text-xs">Date:</span>
                            <span className="font-bold text-slate-900 text-xs text-right">{currentDate.toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span className="text-slate-700 font-semibold text-xs">Due Date:</span>
                            <span className="font-bold text-slate-900 text-xs text-right">
                              {isExtendedInvoice 
                                ? `${currentDate.toLocaleDateString()} (Immediate)` 
                                : new Date(currentDate.getTime() + 30*24*60*60*1000).toLocaleDateString()}
                            </span>
                          </div>
                        </>
                      )}
                      {shouldShowPaymentStatus() && (
                        <div className="flex justify-between gap-2 items-center pt-2 border-t border-slate-200">
                          <span className="text-slate-700 font-semibold text-xs">Status:</span>
                          <span className={`px-2 py-1 ${isExtendedInvoice ? 'bg-red-100 text-red-800 border-red-200' : 'bg-green-100 text-green-800 border-green-200'} border rounded-full text-xs font-bold`}>
                            {invoice.rentalPaymentStatus === 'paid' || invoice.status === 'paid' ? 'PAID' : 'DUE'}
                          </span>
                        </div>
                      )}
                      {isExtendedInvoice && invoice.daysOverdue && (
                        <div className="mt-2 text-center bg-red-600 text-white px-2 py-1.5 rounded-lg">
                          <span className="text-xs font-bold">
                            ⚠️ {invoice.daysOverdue} Days Overdue
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {/* ========================================== */}
                {/* ⭐ RENTAL PERIOD WITH LOCATIONS ⭐ */}
                {/* ========================================== */}
                {shouldShowRentalPeriod() && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-base">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                        <Calendar className="text-white" size={14} />
                      </div>
                      🗓️ Rental Period
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Pickup Date & Location */}
                      <div 
                        className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                        style={getBorderInlineStyle('pickup')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <Calendar className="text-green-600" size={16} />
                          </div>
                          <p className="text-green-700 font-bold text-sm uppercase tracking-wide">Pickup</p>
                        </div>
                        <p className="font-bold text-slate-900 text-sm mb-2">{formatDate(invoice.pickupDateTime)}</p>
                        
                        {/* ⭐ PICKUP LOCATION BOX ⭐ */}
                        <div className="bg-green-50 rounded-lg p-2 border border-green-200 mt-2">
                          <div className="flex items-start gap-2">
                            <MapPin size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-green-900">Pickup Location</p>
                              <p className="text-sm text-slate-900 font-medium">{deliveryCharges.pickup.location}</p>
                              {deliveryCharges.pickup.charge > 0 && (
                                <p className="text-xs text-green-600 font-semibold mt-0.5">
                                  Delivery Charge: +${deliveryCharges.pickup.charge.toFixed(2)}
                                </p>
                              )}
                              {deliveryCharges.pickup.charge === 0 && (
                                <p className="text-xs text-green-600 font-medium mt-0.5">
                                  ✓ No delivery charge
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Return Date & Location */}
                      <div 
                        className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                        style={getBorderInlineStyle('return')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                            <Calendar className="text-red-600" size={16} />
                          </div>
                          <p className="text-red-700 font-bold text-sm uppercase tracking-wide">Return</p>
                        </div>
                        <p className="font-bold text-slate-900 text-sm mb-2">{formatDate(invoice.dropoffDateTime)}</p>
                        
                        {/* ⭐ DROPOFF LOCATION BOX ⭐ */}
                        <div className="bg-blue-50 rounded-lg p-2 border border-blue-200 mt-2">
                          <div className="flex items-start gap-2">
                            <MapPin size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-blue-900">Drop-off Location</p>
                              <p className="text-sm text-slate-900 font-medium">{deliveryCharges.dropoff.location}</p>
                              {deliveryCharges.dropoff.charge > 0 && (
                                <p className="text-xs text-blue-600 font-semibold mt-0.5">
                                  Delivery Charge: +${deliveryCharges.dropoff.charge.toFixed(2)}
                                </p>
                              )}
                              {deliveryCharges.dropoff.charge === 0 && (
                                <p className="text-xs text-blue-600 font-medium mt-0.5">
                                  ✓ No delivery charge
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    
                  </div>
                )}

              </div>

              {/* Services Table - WITH DELIVERY CHARGES ROW */}
              {shouldShowPriceBreakdown() && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm mb-3 overflow-x-auto">
                  <div className="bg-slate-800 text-white p-2">
                    <h3 className="font-bold text-sm">
                      {isExtendedInvoice ? 'Extended Period Charges' : 'Service Details'}
                    </h3>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase tracking-wide">Description</th>
                          <th className="px-3 sm:px-4 py-2 text-center text-xs font-bold text-slate-700 uppercase tracking-wide">Quantity</th>
                          <th className="px-3 sm:px-4 py-2 text-right text-xs font-bold text-slate-700 uppercase tracking-wide">Rate</th>
                          <th className="px-3 sm:px-4 py-2 text-right text-xs font-bold text-slate-700 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {/* Main Service */}
                        <tr className="hover:bg-slate-50">
                          <td className="px-3 sm:px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 ${isExtendedInvoice ? 'bg-red-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <MotorbikeIcon className={`${isExtendedInvoice ? 'text-red-600' : 'text-blue-600'}`} size={16} />
                              </div>
                              <div>
                                <p className="text-xs text-slate-600">{invoice.motorbikeNumber || 'Vehicle N/A'}</p>
                                {invoice.bikeModel && invoice.bikeModel !== 'Unknown' && (
                                  <p className="text-xs text-orange-700 font-medium">Model: {invoice.bikeModel}</p>
                                )}
                                {reservationNumber && (
                                  <p className="text-xs text-orange-600 mt-0.5">
                                    Booking Reference: {reservationNumber}
                                  </p>
                                )}
                                {isExtendedInvoice && invoice.originalReturnDate && (
                                  <p className="text-xs text-red-600 mt-0.5">
                                    ⚠️ Overdue from {new Date(invoice.originalReturnDate).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 ${isExtendedInvoice ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'} rounded-full text-xs font-medium whitespace-nowrap`}>
                              {isExtendedInvoice && invoice.daysOverdue 
                                ? `${invoice.daysOverdue} day${invoice.daysOverdue !== 1 ? 's' : ''}` 
                                : `${rentalDays} day${rentalDays !== 1 ? 's' : ''}`}
                            </span>
                          </td>
                          <td className="px-3 sm:px-4 py-2 text-right font-bold text-slate-900 text-xs">
                            ${isExtendedInvoice && invoice.dailyRateApplied 
                              ? invoice.dailyRateApplied.toFixed(2) 
                              : dailyRate.toFixed(2)}
                          </td>
                          <td className="px-3 sm:px-4 py-2 text-right font-bold text-sm text-slate-900">
                            ${rentalAmount.toFixed(2)}
                          </td>
                        </tr>

                        {/* Add-ons */}
                        {shouldShowAddons() && !isExtendedInvoice && invoice.selectedAddons?.map((addon, index) => (
                          <tr key={index} className="hover:bg-slate-50">
                            <td className="px-3 sm:px-4 py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Shield className="text-purple-600" size={16} />
                                </div>
                                <div>
                                  <h4 className="font-bold text-slate-900 text-xs">{addon.name || 'Add-on Service'}</h4>
                                  <p className="text-xs text-slate-600">{addon.category || 'Additional Service'}</p>
                                  <p className="text-xs text-slate-500">
                                    {addon.pricingMethod === 'perDay' 
                                      ? `Daily rate: $${addon.price}/day` 
                                      : 'One-time fee'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-center">
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium whitespace-nowrap">
                                {addon.pricingMethod === 'perDay' ? `${rentalDays} day${rentalDays !== 1 ? 's' : ''}` : '1x'}
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-right font-bold text-slate-900 text-xs">
                              ${parseFloat(addon.price || 0).toFixed(2)}
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-right font-bold text-sm text-slate-900">
                              ${parseFloat(addon.calculatedCost || addon.price || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}

                        {/* ⭐ DELIVERY CHARGES ROW ⭐ */}
                        {deliveryCharges.hasCharges && (
                          <tr className="hover:bg-slate-50 bg-purple-50">
                            <td className="px-3 sm:px-4 py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <MapPin className="text-purple-600" size={16} />
                                </div>
                                <div>
                                  <h4 className="font-bold text-slate-900 text-xs">🚗 Delivery Charges</h4>
                                  
                                  
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-center">
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium whitespace-nowrap">
                                Service
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-right font-bold text-slate-900 text-xs">
                              -
                            </td>
                            <td className="px-3 sm:px-4 py-2 text-right font-bold text-sm text-purple-900">
                              ${deliveryCharges.total.toFixed(2)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* UPLOADED DOCUMENTS SECTION */}
              {uploadedDocs.length > 0 && shouldShowDocuments() && (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                      <FileText className="text-white" size={16} />
                    </div>
                    <h3 className="font-bold text-purple-900 text-base">
                      📎 Uploaded Documents ({uploadedDocs.length})
                    </h3>
                    {invoice.documentsLoadedFromReservation && (
                      <span className="ml-auto bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full border border-blue-200">
                        From Reservation
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {uploadedDocs.map((doc) => {
                      const colors = getCategoryColor(doc.category);
                      return (
                        <div 
                          key={doc.id}
                          className={`${colors.bg} ${colors.border} border-2 rounded-lg p-3 hover:shadow-md transition-shadow`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-10 h-5 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0 border ${colors.border}`}>
                              <FileText className={colors.icon} size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-bold ${colors.text} text-sm truncate`}>
                                {doc.documentName}
                              </h4>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-3 bg-white rounded-lg p-2 border border-purple-200">
                    <p className="text-xs text-slate-600 flex items-center gap-2">
                      <CheckCircle size={12} className="text-green-600" />
                      <span>
                        <strong>{uploadedDocs.filter(d => d.status === 'uploaded' || d.status === 'existing').length}</strong> document
                        {uploadedDocs.filter(d => d.status === 'uploaded' || d.status === 'existing').length !== 1 ? 's' : ''} stored 
                        securely in Firebase Storage
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Summary Section - WITH DELIVERY CHARGES */}
              <div className="grid grid-cols-1 gap-3 mb-3">
                
                {/* 💰 TOTAL SUMMARY - WITH DELIVERY */}
                {shouldShowInvoiceSummary() && (
                  <div className={`${isExtendedInvoice ? 'bg-gradient-to-br from-red-800 to-orange-900' : 'bg-gradient-to-br from-slate-800 to-slate-900'} text-white rounded-lg p-3`}>
                    <h3 className="font-bold text-base mb-2 flex items-center gap-2">
                      <DollarSign className={`${isExtendedInvoice ? 'text-orange-400' : 'text-green-400'}`} size={16} />
                      {isExtendedInvoice ? 'Extended Charges Total' : 'Invoice Summary'}
                    </h3>
                    
                    <div className="space-y-1.5 mb-3">
                      {!isExtendedInvoice && (
                        <>
                          <div className="flex justify-between items-center text-slate-300 text-xs">
                            <span>Rental Amount:</span>
                            <span className="font-medium">${rentalAmount.toFixed(2)}</span>
                          </div>
                          {addonTotal > 0 && (
                            <div className="flex justify-between items-center text-slate-300 text-xs">
                              <span>Add-ons Total:</span>
                              <span className="font-medium">${addonTotal.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* ⭐ DELIVERY CHARGES IN SUMMARY ⭐ */}
                          {deliveryCharges.hasCharges && (
                            <div className="flex justify-between items-center text-slate-300 text-xs">
                              <div className="flex items-center gap-1">
                                <MapPin size={10} />
                                <span>Delivery Charges:</span>
                              </div>
                              <span className="font-medium">${deliveryCharges.total.toFixed(2)}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center text-slate-300 border-t border-slate-600 pt-1.5 text-xs">
                            <span className="font-semibold">Subtotal:</span>
                            <span className="font-semibold">${(rentalAmount + addonTotal + deliveryCharges.total).toFixed(2)}</span>
                          </div>
                          
                          {shouldShowDiscount() && hasDiscount && (
                            <div className="flex justify-between items-center bg-green-500/20 rounded-lg p-1.5 border border-green-500/30">
                              <div className="flex items-center gap-1.5">
                                <Gift size={12} className="text-green-400" />
                                <span className="text-green-300 font-semibold text-xs">Discount:</span>
                              </div>
                              <span className="font-bold text-green-400 text-sm">-${discountAmount.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {isExtendedInvoice && invoice.dailyRateApplied && invoice.daysOverdue && (
                        <div className="bg-red-500/20 rounded p-1.5">
                          <div className="text-orange-200 text-xs">
                            ${invoice.dailyRateApplied}/day × {invoice.daysOverdue} extended days
                          </div>
                        </div>
                      )}
                      {shouldShowTotalAmount() && (
                        <div className="border-t-2 border-slate-600 pt-1.5">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-bold">Total Amount:</span>
                            <span className={`text-xl sm:text-2xl font-black ${isExtendedInvoice ? 'text-orange-400' : 'text-green-400'}`}>
                              ${totalAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {shouldShowPaymentStatus() && (
                      <div className={`${isExtendedInvoice ? 'bg-red-500/20 border-red-500/30' : 'bg-green-500/20 border-green-500/30'} border rounded-lg p-2 text-center`}>
                        <div className={`flex items-center justify-center gap-1.5 ${isExtendedInvoice ? 'text-orange-300' : 'text-green-300'} text-sm`}>
                          <CheckCircle size={14} />
                          <span className="font-bold">
                            {invoice.rentalPaymentStatus === 'paid' || invoice.status === 'paid' ? 'PAYMENT RECEIVED' : 'PAYMENT DUE'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Payment Method */}
                    <div className="mt-2 pt-2 border-t border-slate-600">
                      <div className="flex items-center justify-between text-slate-300 text-sm">
                        <span className="font-semibold">Payment Method:</span>
                        <span className="font-medium">{invoice.paymentMethod || 'Cash/Card'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ⭐⭐⭐ MULTI-CURRENCY SECURITY DEPOSIT - STANDALONE HIGHLIGHTED BOX ⭐⭐⭐ */}
                {shouldShowDepositDisplay() && depositInfo.value > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-3 border-green-400 rounded-xl p-4 shadow-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-black text-green-900 text-lg flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                          <Shield className="text-white" size={16} />
                        </div>
                        💰 Security Deposit
                      </h3>
                      <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1">
                        <Globe size={10} />
                        {depositInfo.currency}
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border-2 border-green-300 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-700 font-bold text-sm uppercase tracking-wide mb-1">Deposit Amount</p>
                          <p className="text-xs text-green-600">Refundable upon vehicle return</p>
                          <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-1">
                            <Globe size={10} />
                            {depositInfo.currency} - {depositInfo.symbol}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-4xl font-black text-green-700">{depositInfo.formatted}</p>
                          <div className="flex items-center justify-end gap-1.5 mt-1">
                            <CheckCircle size={14} className="text-green-600" />
                            <span className="text-xs font-semibold text-green-600">Payment Received</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 bg-green-100 rounded-lg p-2 border border-green-300">
                      <p className="text-xs text-green-800 font-medium text-center">
                        ✓ This deposit ({depositInfo.formatted} {depositInfo.currency}) will be refunded after vehicle inspection upon return
                      </p>
                    </div>
                  </div>
                )}

                {/* Terms & Conditions Section */}
                {shouldShowTerms() && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    {shouldShowTermsHeading() && (
                      <h3 className="font-black text-red-900 text-base mb-3 uppercase tracking-wide flex items-center gap-2">
                        <AlertCircle className="text-red-600" size={16} />
                        {getTermsHeading()}
                      </h3>
                    )}
                    <div className="space-y-2">
                      {getTermsText().split('\n').filter(line => line.trim()).map((line, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-2 bg-red-100 rounded-lg p-2 border border-red-200"
                        >
                          <div className="w-2 h-2 bg-red-600 rounded-full mt-1 flex-shrink-0"></div>
                          <p className="text-xs text-red-800 font-medium flex-1">{line}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* QR Code & Barcode Section */}
                {(shouldShowQRCode() || shouldShowBarcode()) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <h3 className="font-bold text-slate-800 text-sm mb-3">📱 Scan Codes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {shouldShowQRCode() && (
                        <div className="flex flex-col items-center">
                          <QRCodeCanvas value={generateQRCodeData()} size={150} />
                          <p className="text-xs text-slate-600 mt-2 text-center">QR Code - Invoice Details</p>
                        </div>
                      )}
                      {shouldShowBarcode() && (
                        <div className="flex flex-col items-center">
                          <BarcodeCanvas value={generateBarcodeData()} width={300} height={80} />
                          <p className="text-xs text-slate-600 mt-2 text-center">Barcode - {invoiceNumber}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Signature Section */}
                {shouldShowSignature() && (
                  <div className="bg-white border border-slate-300 rounded-lg p-3">
                    <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
                      <FileText className="text-slate-600" size={14} />
                      Customer Signature
                    </h3>
                    
                    <div className="max-w-md">
                      <div className="border-b-2 border-slate-300 h-16 mb-1"></div>
                      <p className="text-xs text-slate-500">Signature</p>
                    </div>
                  </div>
                )}
              
              </div>

              {/* Footer */}
              {shouldShowFooterSection() && (
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
                    <div className="text-xs text-slate-600">
                      <p><strong>Generated by:</strong> {currentUser?.username || 'System Administrator'}</p>
                      <p><strong>Generated on:</strong> {currentDate.toLocaleString()}</p>
                      <p><strong>Source:</strong> {sourceComponent === 'reservations' ? 'Reservation System' : 'Invoice Manager'}</p>
                      {isExtendedInvoice && (
                        <p className="text-red-600 font-medium">Extended rental charges - {isAutomaticInvoice ? 'automatically' : 'manually'} calculated</p>
                      )}
                      {shouldShowDepositDisplay() && depositInfo.value > 0 && (
                        <p className="text-green-600 font-medium">Security Deposit: {depositInfo.formatted} {depositInfo.currency}</p>
                      )}
                      {deliveryCharges.hasCharges && (
                        <p className="text-purple-600 font-medium">
                          Delivery Charges: ${deliveryCharges.total.toFixed(2)} 
                          (Pickup: {deliveryCharges.pickup.location}, Drop-off: {deliveryCharges.dropoff.location})
                        </p>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-600">
                      <p>Thank you for your business!</p>
                    </div>
                  </div>
                  
                  <div className="text-center text-xs text-slate-500 bg-slate-50 py-2 rounded-lg">
                    <p>
                      {shouldShowSignature() 
                        ? 'Please sign above to acknowledge receipt and acceptance of terms.' 
                        : 'This is a computer-generated invoice. No signature required.'}
                    </p>
                    <p className="mt-0.5">Questions? Contact us at {getCompanyEmail()} or {getCompanyPhone()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedInvoicePreviewPage;